import React from 'react';
import { Calendar, AlertTriangle, Clock, TrendingUp } from 'lucide-react';

interface DeadlineStats {
  overdue: number;
  today: number;
  thisWeek: number;
  upcoming30Days: number;
}

interface DeadlineSummaryCardProps {
  stats: DeadlineStats;
  loading?: boolean;
  onSectionClick?: (section: 'overdue' | 'today' | 'thisWeek' | 'upcoming') => void;
}

export function DeadlineSummaryCard({ stats, loading, onSectionClick }: DeadlineSummaryCardProps) {
  const sections = [
    {
      id: 'overdue' as const,
      label: 'Overdue',
      value: stats.overdue,
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-50',
      iconColor: 'text-red-500',
      borderColor: 'border-red-200',
    },
    {
      id: 'today' as const,
      label: 'Today',
      value: stats.today,
      icon: Clock,
      color: 'text-orange-600 bg-orange-50',
      iconColor: 'text-orange-500',
      borderColor: 'border-orange-200',
    },
    {
      id: 'thisWeek' as const,
      label: 'This Week',
      value: stats.thisWeek,
      icon: Calendar,
      color: 'text-blue-600 bg-blue-50',
      iconColor: 'text-blue-500',
      borderColor: 'border-blue-200',
    },
    {
      id: 'upcoming' as const,
      label: 'Next 30 Days',
      value: stats.upcoming30Days,
      icon: TrendingUp,
      color: 'text-green-600 bg-green-50',
      iconColor: 'text-green-500',
      borderColor: 'border-green-200',
    },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Deadline Summary</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 rounded-lg h-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Deadline Summary</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {sections.map((section) => {
          const Icon = section.icon;
          const hasItems = section.value > 0;
          
          return (
            <button
              key={section.id}
              onClick={() => hasItems && onSectionClick?.(section.id)}
              disabled={!hasItems}
              className={`
                relative overflow-hidden rounded-lg border transition-all
                ${hasItems 
                  ? `${section.borderColor} hover:shadow-md cursor-pointer` 
                  : 'border-gray-200 cursor-default opacity-60'
                }
              `}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`w-5 h-5 ${hasItems ? section.iconColor : 'text-gray-400'}`} />
                  {section.id === 'overdue' && hasItems && (
                    <span className="flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                    </span>
                  )}
                </div>
                <div className="text-2xl font-bold text-gray-900">{section.value}</div>
                <div className="text-sm text-gray-600">{section.label}</div>
              </div>
              {hasItems && (
                <div className={`absolute inset-x-0 bottom-0 h-1 ${section.color.split(' ')[1]}`} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}